import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { feature } from 'topojson-client';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveWorldMap = ({ 
  data = [],
  selectedMetric = 'scans',
  selectedTimeRange = 'last7days',
  selectedDeviceType = 'all',
  isLoading = false,
  onRegionClick,
  onCountryClick,
  className = ''
}) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [mapCenter, setMapCenter] = useState({ x: 0, y: 0 });
  const [geoData, setGeoData] = useState(null);

  // Load geography data
  useEffect(() => {
    import('world-atlas/countries-110m.json')
      .then(topology => {
        const countries = feature(topology, topology.objects.countries);
        console.log('🗺️ Geography data loaded successfully:', countries.features.length, 'countries');
        console.log('🗺️ First country properties:', countries.features[0]?.properties);
        setGeoData(countries);
      })
      .catch(error => {
        console.error('🗺️ Failed to load geography data:', error);
        // Fallback to CDN
        setGeoData("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json");
      });
  }, []);

  // Debug: Log the data being passed to the map
  console.log('🗺️ InteractiveWorldMap received data:', data);
  console.log('🗺️ Data length:', data?.length);
  console.log('🗺️ First few countries:', data?.slice(0, 3));
  console.log('🗺️ All country codes in data:', data?.map(d => d?.code));
  console.log('🗺️ All country names in data:', data?.map(d => d?.name));

  // Country center positions relative to the world map image (as percentages)
  // These coordinates are approximate positions based on a standard world map projection
  const worldCountries = {
    // North America
    'US': { center: { x: 20, y: 45 }, name: 'United States' },
    'CA': { center: { x: 18, y: 30 }, name: 'Canada' },
    'MX': { center: { x: 17, y: 55 }, name: 'Mexico' },
    
    // Europe
    'GB': { center: { x: 48, y: 38 }, name: 'United Kingdom' },
    'FR': { center: { x: 50, y: 42 }, name: 'France' },
    'DE': { center: { x: 52, y: 38 }, name: 'Germany' },
    'ES': { center: { x: 47, y: 48 }, name: 'Spain' },
    'IT': { center: { x: 53, y: 48 }, name: 'Italy' },
    'NL': { center: { x: 51, y: 36 }, name: 'Netherlands' },
    'RU': { center: { x: 68, y: 32 }, name: 'Russia' },
    'SE': { center: { x: 54, y: 28 }, name: 'Sweden' },
    'NO': { center: { x: 52, y: 25 }, name: 'Norway' },
    'FI': { center: { x: 56, y: 26 }, name: 'Finland' },
    'PL': { center: { x: 55, y: 36 }, name: 'Poland' },
    
    // Asia
    'CN': { center: { x: 73, y: 42 }, name: 'China' },
    'JP': { center: { x: 82, y: 42 }, name: 'Japan' },
    'IN': { center: { x: 66, y: 55 }, name: 'India' },
    'KR': { center: { x: 80, y: 44 }, name: 'South Korea' },
    'SG': { center: { x: 74, y: 65 }, name: 'Singapore' },
    'TH': { center: { x: 72, y: 58 }, name: 'Thailand' },
    'VN': { center: { x: 74, y: 58 }, name: 'Vietnam' },
    'MY': { center: { x: 73, y: 63 }, name: 'Malaysia' },
    'ID': { center: { x: 76, y: 68 }, name: 'Indonesia' },
    'PH': { center: { x: 78, y: 58 }, name: 'Philippines' },
    
    // Oceania
    'AU': { center: { x: 80, y: 75 }, name: 'Australia' },
    'NZ': { center: { x: 87, y: 82 }, name: 'New Zealand' },
    
    // South America
    'BR': { center: { x: 35, y: 70 }, name: 'Brazil' },
    'AR': { center: { x: 32, y: 82 }, name: 'Argentina' },
    'CL': { center: { x: 28, y: 80 }, name: 'Chile' },
    'PE': { center: { x: 28, y: 72 }, name: 'Peru' },
    'CO': { center: { x: 28, y: 63 }, name: 'Colombia' },
    'VE': { center: { x: 32, y: 60 }, name: 'Venezuela' },
    
    // Africa
    'ZA': { center: { x: 56, y: 82 }, name: 'South Africa' },
    'NG': { center: { x: 51, y: 62 }, name: 'Nigeria' },
    'EG': { center: { x: 58, y: 52 }, name: 'Egypt' },
    'KE': { center: { x: 59, y: 65 }, name: 'Kenya' },
    'MA': { center: { x: 47, y: 50 }, name: 'Morocco' },
    'GH': { center: { x: 49, y: 62 }, name: 'Ghana' },
    'ET': { center: { x: 60, y: 62 }, name: 'Ethiopia' },
    
    // Middle East
    'AE': { center: { x: 62, y: 55 }, name: 'UAE' },
    'SA': { center: { x: 61, y: 55 }, name: 'Saudi Arabia' },
    'IL': { center: { x: 58, y: 50 }, name: 'Israel' },
    'TR': { center: { x: 58, y: 48 }, name: 'Turkey' }
  };

  const getMetricValue = (country) => {
    switch (selectedMetric) {
      case 'scans': return country?.scans || 0;
      case 'conversions': return country?.conversions || 0;
      case 'engagement': return parseFloat(country?.conversionRate || 0);
      default: return country?.scans || 0;
    }
  };

  const getMetricLabel = () => {
    switch (selectedMetric) {
      case 'scans': return 'Total Scans';
      case 'conversions': return 'Conversions';
      case 'engagement': return 'Engagement Rate';
      default: return 'Total Scans';
    }
  };

  // Calculate bubble size based on scan data - more proportional sizing
  const getBubbleRadius = (country) => {
    if (!data || data?.length === 0) return 0;
    
    const maxValue = Math.max(...data?.map(d => getMetricValue(d)));
    const countryValue = getMetricValue(country);
    
    if (!maxValue || maxValue === 0 || !countryValue) return 0;
    
    // Proportional bubble sizing: minimum 8px, maximum 40px
    const minRadius = 8;
    const maxRadius = 40;
    const ratio = countryValue / maxValue;
    
    return minRadius + (ratio * (maxRadius - minRadius));
  };

  const handleRegionHover = (region) => {
    setHoveredRegion(region);
  };

  const handleRegionClick = (region) => {
    if (onRegionClick) {
      onRegionClick(region);
    }
    if (onCountryClick) {
      onCountryClick(region?.code);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleResetView = () => {
    setZoomLevel(1);
    setMapCenter({ x: 0, y: 0 });
  };

  const getCountryData = (countryCode) => {
    return data?.find(country => country?.code === countryCode);
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
      {/* Map Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">Interactive World Map</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Icon name="Globe" size={16} />
            <span>{getMetricLabel()}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Map Info */}
          <div className="flex items-center bg-blue-50 rounded-lg px-3 py-1">
            <Icon name="Map" size={14} className="mr-1 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">Color Intensity = Scan Count</span>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              className="p-1"
            >
              <Icon name="ZoomIn" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              className="p-1"
            >
              <Icon name="ZoomOut" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetView}
              className="p-1"
            >
              <Icon name="RotateCcw" size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative h-96 bg-gradient-to-b from-blue-200 via-blue-100 to-blue-50 overflow-hidden">
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <div className="flex items-center space-x-2">
              <Icon name="Loader2" size={20} className="animate-spin text-blue-600" />
              <span className="text-sm text-gray-700">Loading map data...</span>
            </div>
          </div>
        )}

        {/* No Data State */}
        {!isLoading && (!data || data?.length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="text-center">
              <Icon name="Globe" size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-700 font-medium">No Geographic Data</p>
              <p className="text-sm text-gray-500 mt-1">
                Scan data will appear as color-coded countries on the map once available
              </p>
            </div>
          </div>
        )}

        {/* Debug: Show data info */}
        {data && data.length > 0 && (
          <div className="absolute top-2 right-2 bg-white/90 px-3 py-2 rounded text-xs text-gray-600 z-10 max-w-xs">
            <div className="font-semibold mb-1">Map Data Debug:</div>
            <div>Countries: {data.length}</div>
            <div>With scans: {data.filter(d => d.scans > 0).length}</div>
            <div>Sample: {data[0]?.name} ({data[0]?.code}) - {data[0]?.scans} scans</div>
            <div className="mt-2 text-green-600 font-semibold">✓ Data loaded</div>
            <button
              onClick={() => {
                console.log('🗺️ Full data structure:', data);
                console.log('🗺️ Data types:', data.map(d => ({ code: typeof d.code, name: typeof d.name, scans: typeof d.scans })));
                console.log('🗺️ Sample data item:', data[0]);
              }}
              className="mt-2 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
            >
              Log Data
            </button>
          </div>
        )}

        {/* Debug Info */}
        {data && data.length > 0 && (
          <div className="absolute top-2 left-2 bg-white/90 px-2 py-1 rounded text-xs text-gray-600 z-10">
            Countries with data: {data.filter(d => d.scans > 0).length}
          </div>
        )}

        {/* Geography Loading Status */}
        <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded text-xs text-gray-600 z-10">
          <div className="font-semibold">Geography Status:</div>
          <div>Loading world map data...</div>
          <div className="text-xs text-red-600 mt-1">
            If map doesn't load, try refreshing the page
          </div>
        </div>

        {/* Real World Map */}
        <div className="w-full h-full">
          <ComposableMap
            projection="geoNaturalEarth1"
            projectionConfig={{
              scale: 120,
              center: [0, 0]
            }}
            width={800}
            height={400}
            className="w-full h-full"
          >
            <ZoomableGroup zoom={zoomLevel} center={[mapCenter?.x, mapCenter?.y]}>
              <Geographies geography={geoData || "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json"}>
                {({ geographies }) => {
                  // Debug: Log geography loading
                  console.log('🗺️ Geography loaded:', geographies?.length, 'countries');
                  if (geographies?.length > 0) {
                    console.log('🗺️ First geography properties:', geographies[0]?.properties);
                  }
                  
                  // Handle case where geography doesn't load
                  if (!geographies || geographies.length === 0) {
                    console.error('🗺️ No geography data loaded');
                    return [];
                  }
                  
                  // Debug: Count matches
                  let matchCount = 0;
                  let totalCountries = geographies.length;
                  
                  const processedGeographies = geographies.map((geo) => {
                    // Try multiple property names for different geography files
                    const countryCode = geo.properties.ISO_A2 || geo.properties.ISO_A2_EH || geo.properties.ISO_A3 || geo.properties.ADM0_A3 || geo.properties.iso_a2 || geo.properties.iso_a3;
                    const countryName = geo.properties.NAME || geo.properties.NAME_LONG || geo.properties.ADMIN || geo.properties.name || geo.properties.NAME_EN;
                    const countryNameLong = geo.properties.NAME_LONG || geo.properties.NAME || geo.properties.ADMIN || geo.properties.name || geo.properties.NAME_EN;
                    
                    // Try multiple matching strategies
                    let countryData = data?.find(d => d?.code === countryCode);
                    if (!countryData) {
                      countryData = data?.find(d => d?.name === countryName);
                    }
                    if (!countryData) {
                      countryData = data?.find(d => d?.name === countryNameLong);
                    }
                    if (!countryData) {
                      // Try case-insensitive matching
                      countryData = data?.find(d => 
                        d?.name?.toLowerCase() === countryName?.toLowerCase() ||
                        d?.name?.toLowerCase() === countryNameLong?.toLowerCase()
                      );
                    }
                    
                    const scanCount = countryData?.scans || 0;
                    
                    // Temporary test: Force some countries to have data for testing
                    let testScanCount = scanCount;
                    if (['US', 'DE', 'GB', 'FR', 'CA', 'AU', 'JP', 'NL'].includes(countryCode) && scanCount === 0) {
                      testScanCount = Math.floor(Math.random() * 1000) + 100; // Random test data
                      console.log(`🧪 TEST: Forcing ${countryCode} to have ${testScanCount} scans for testing`);
                    }
                    
                    // Count matches
                    if (countryData) {
                      matchCount++;
                    }
                    
                    // Debug: Log country matching
                    if (countryData && scanCount > 0) {
                      console.log(`🗺️ Found country data for ${countryCode} (${countryName}):`, countryData);
                    }
                    
                    // Debug: Log all countries for first few iterations
                    if (geographies.indexOf(geo) < 5) {
                      console.log(`🗺️ Geography ${geographies.indexOf(geo)}:`, {
                        code: countryCode,
                        name: countryName,
                        nameLong: countryNameLong,
                        hasData: !!countryData,
                        scanCount,
                        matchedBy: countryData ? 
                          (countryData.code === countryCode ? 'code' : 
                           countryData.name === countryName ? 'name' : 
                           countryData.name === countryNameLong ? 'nameLong' : 'case-insensitive') : 'none',
                        availableProperties: Object.keys(geo.properties || {}),
                        sampleProperties: {
                          ISO_A2: geo.properties?.ISO_A2,
                          NAME: geo.properties?.NAME,
                          ADMIN: geo.properties?.ADMIN,
                          name: geo.properties?.name,
                          iso_a2: geo.properties?.iso_a2
                        }
                      });
                    }
                    
                    // Debug: Log specific countries we're looking for
                    if (['US', 'DE', 'GB', 'FR', 'CA', 'AU', 'JP', 'NL'].includes(countryCode)) {
                      console.log(`🗺️ Looking for ${countryCode} (${countryName}):`, {
                        found: !!countryData,
                        scanCount,
                        dataItem: countryData,
                        availableData: data?.map(d => ({ code: d.code, name: d.name, scans: d.scans }))
                      });
                    }
                    
                    // Color coding based on scan count with more granular ranges
                    const getCountryColor = (scans) => {
                      if (scans === 0) return "#f3f4f6"; // Light gray for no data
                      if (scans < 10) return "#fef3c7"; // Very light yellow
                      if (scans < 20) return "#fde68a"; // Light yellow
                      if (scans < 50) return "#fbbf24"; // Yellow
                      if (scans < 100) return "#f59e0b"; // Orange
                      if (scans < 250) return "#d97706"; // Dark orange
                      if (scans < 500) return "#ea580c"; // Red-orange
                      if (scans < 1000) return "#dc2626"; // Red
                      if (scans < 3000) return "#b91c1c"; // Dark red
                      return "#7f1d1d"; // Darkest red for highest counts
                    };
                    
                    const getStrokeColor = (scans) => {
                      if (scans === 0) return "#d1d5db";
                      if (scans < 10) return "#f59e0b";
                      if (scans < 100) return "#d97706";
                      return "#7f1d1d";
                    };
                    
                    const countryColor = getCountryColor(testScanCount);
                    const strokeColor = getStrokeColor(testScanCount);
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={countryColor}
                        stroke={strokeColor}
                        strokeWidth={0.5}
                        style={{
                          default: {
                            fill: countryColor,
                            stroke: strokeColor,
                            strokeWidth: 0.5,
                            outline: "none",
                          },
                          hover: {
                            fill: testScanCount > 0 ? "#1e40af" : "#9ca3af",
                            stroke: "#111827",
                            strokeWidth: 1,
                            outline: "none",
                          },
                          pressed: {
                            fill: testScanCount > 0 ? "#1e3a8a" : "#6b7280",
                            stroke: "#000000",
                            strokeWidth: 1,
                            outline: "none",
                          },
                        }}
                        onMouseEnter={() => countryData && handleRegionHover(countryData)}
                        onMouseLeave={() => setHoveredRegion(null)}
                        onClick={() => countryData && handleRegionClick(countryData)}
                      />
                    );
                  });
                  
                  // Debug: Log match summary
                  console.log(`🗺️ Map processing complete: ${matchCount}/${totalCountries} countries matched with data`);
                  
                  return processedGeographies;
                }}
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
        </div>

        {/* Enhanced Hover Tooltip */}
        {hoveredRegion && (
          <div className="absolute top-4 left-4 bg-white border border-gray-200 rounded-lg p-4 shadow-xl z-20 min-w-60">
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-xl">{hoveredRegion?.flag || '🌍'}</span>
              <span className="font-semibold text-gray-900">
                {worldCountries?.[hoveredRegion?.code]?.name || hoveredRegion?.code}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Scans:</span>
                <span className="font-bold text-blue-600">{hoveredRegion?.scans?.toLocaleString() || '0'}</span>
              </div>
              {hoveredRegion?.conversions && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Conversions:</span>
                  <span className="font-bold text-green-600">{hoveredRegion?.conversions?.toLocaleString()}</span>
                </div>
              )}
              {hoveredRegion?.conversionRate && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Conversion Rate:</span>
                  <span className="font-bold text-purple-600">{hoveredRegion?.conversionRate}%</span>
                </div>
              )}
              {hoveredRegion?.percentage && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Market Share:</span>
                  <span className="font-bold text-orange-600">{hoveredRegion?.percentage}%</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Color Legend */}
        <div className="absolute bottom-4 right-4 bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
          <div className="text-xs text-gray-600">
            <div className="font-medium mb-3">Scan Count by Country</div>
            <div className="space-y-1.5">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#7f1d1d" }}></div>
                <span>3000+ scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#b91c1c" }}></div>
                <span>1000-2999 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#dc2626" }}></div>
                <span>500-999 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#ea580c" }}></div>
                <span>250-499 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#d97706" }}></div>
                <span>100-249 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#f59e0b" }}></div>
                <span>50-99 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#fbbf24" }}></div>
                <span>20-49 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#fde68a" }}></div>
                <span>10-19 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#fef3c7" }}></div>
                <span>1-9 scans</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "#f3f4f6" }}></div>
                <span>No data</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-gray-200 text-gray-500">
              Countries with data: {data?.filter(d => d?.scans > 0)?.length || 0}
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="absolute top-4 right-4 text-xs text-gray-500 bg-white/90 px-2 py-1 rounded border border-gray-200">
          Updated: {new Date()?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </div>

        {/* Map Attribution */}
        <div className="absolute bottom-4 left-4 text-xs text-gray-400">
          Interactive geographic visualization
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default InteractiveWorldMap;